import React, { useState } from 'react';
import { Empty, ErrorText, Panel, useChrome } from './ceoChrome';

const EMPTY = {
  title: '',
  body: '',
  audience: 'all',
  kind: 'banner',
  startsAt: '',
  endsAt: '',
  dismissible: true,
};

export default function AnnouncementsPanel({ colors, narrow, data, error, onAction, busy }) {
  const chrome = useChrome(colors, narrow);
  const [form, setForm] = useState(EMPTY);
  const [pending, setPending] = useState(false);
  const [pendingEnd, setPendingEnd] = useState('');
  const [localError, setLocalError] = useState('');
  const rows = (data && data.announcements) || [];

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setPending(false);
  }

  async function create() {
    if (!onAction) return;
    setLocalError('');
    try {
      await onAction({ action: 'create', ...form, confirm: true });
      setForm(EMPTY);
      setPending(false);
    } catch (err) {
      setLocalError(err.message || 'Could not publish');
    }
  }

  return (
    <div style={chrome.page} data-testid="ceo-announcements">
      <div>
        <h2 style={chrome.title}>Announcements</h2>
        <p style={chrome.sub}>A banner for agents or homeowners, or a What's new card. Set a start and an optional end. People can dismiss it.</p>
      </div>
      {error || localError ? <ErrorText>{error || localError}</ErrorText> : null}
      <Panel chrome={chrome} title="New announcement">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input aria-label="Title" value={form.title} onChange={(event) => set('title', event.target.value)} placeholder="Title" style={chrome.field} />
          <textarea aria-label="Message" value={form.body} onChange={(event) => set('body', event.target.value)} placeholder="Message" style={chrome.area} />
          <div style={{ display: 'flex', gap: 8, flexDirection: narrow ? 'column' : 'row' }}>
            <select aria-label="Audience" value={form.audience} onChange={(event) => set('audience', event.target.value)} style={chrome.field}>
              <option value="all">All agents</option>
              <option value="solo">Solo</option>
              <option value="team">Team</option>
              <option value="trial">Trial</option>
              <option value="homeowners">Homeowners</option>
            </select>
            <select aria-label="Kind" value={form.kind} onChange={(event) => set('kind', event.target.value)} style={chrome.field}>
              <option value="banner">Banner</option>
              <option value="release">What's new</option>
            </select>
          </div>
          <label style={{ color: chrome.secondary, fontSize: 13 }}>
            Starts
            <input aria-label="Starts" type="datetime-local" value={form.startsAt} onChange={(event) => set('startsAt', event.target.value)} style={{ ...chrome.field, marginTop: 6 }} />
          </label>
          <label style={{ color: chrome.secondary, fontSize: 13 }}>
            Ends
            <input aria-label="Ends" type="datetime-local" value={form.endsAt} onChange={(event) => set('endsAt', event.target.value)} style={{ ...chrome.field, marginTop: 6 }} />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44 }}>
            <input type="checkbox" checked={form.dismissible} onChange={(event) => set('dismissible', event.target.checked)} />
            Dismissible
          </label>
          {pending ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={chrome.button} disabled={busy} onClick={create}>Publish</button>
              <button type="button" style={chrome.quiet} onClick={() => setPending(false)}>Cancel</button>
            </div>
          ) : (
            <button type="button" style={chrome.button} onClick={() => setPending(true)} disabled={!form.title.trim() || !form.body.trim()}>Publish</button>
          )}
        </div>
      </Panel>
      {rows.length === 0 && data ? <Empty chrome={chrome}>No announcements yet.</Empty> : null}
      {rows.map((row) => (
        <Panel key={row.id} chrome={chrome} title={row.title}>
          <div style={{ fontWeight: 600 }}>{row.kind === 'release' ? "What's new" : 'Banner'} · {row.title}</div>
          <p style={chrome.sub}>{row.body}</p>
          <div style={{ color: chrome.secondary, fontSize: 14 }}>{row.audience}{row.dismissible ? ' · Dismissible' : ''}</div>
          {onAction && pendingEnd === row.id ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="button" style={chrome.button} onClick={() => onAction({ action: 'end', id: row.id, confirm: true })}>End</button>
              <button type="button" style={chrome.quiet} onClick={() => setPendingEnd('')}>Cancel</button>
            </div>
          ) : null}
          {onAction && pendingEnd !== row.id ? (
            <button type="button" style={{ ...chrome.quiet, marginTop: 12 }} onClick={() => setPendingEnd(row.id)}>End now</button>
          ) : null}
        </Panel>
      ))}
    </div>
  );
}
